import { Middleware } from 'redux';
import { websocketActions } from './websocketSlice';
// import { notificationActions } from './notificationsSlice';
import { getBadgeAchievementsEvents } from './getBadgeAchievementSlice';
import { getBadgesEvents } from './getBadgeSlice';
import { getCurrentCellStore } from '../utils/util';
import { logoutEvents } from './logoutSlice';
import { Auth } from 'aws-amplify';
import { BADGE_FILTER_CONFIGS } from '../constants/constants';

const websocketMiddleware: Middleware = (store) => {
  let socket: WebSocket | null = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  let reconnectInterval: NodeJS.Timeout | null = null;
  let isManualDisconnect = false;

  const getReconnectDelay = (attempt: number) => {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    return Math.min(1000 * Math.pow(2, attempt), 30000);
  };

  const clearReconnectTimer = () => {
    if (reconnectInterval) {
      clearTimeout(reconnectInterval);
      reconnectInterval = null;
    }
  };

  const attemptReconnect = () => {
    if (isManualDisconnect || reconnectAttempts >= maxReconnectAttempts) {
      console.log('WebSocket reconnection stopped:', isManualDisconnect ? 'Manual disconnect' : 'Max attempts reached');
      return;
    }

    const delay = getReconnectDelay(reconnectAttempts);
    console.log(`WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
    
    reconnectInterval = setTimeout(() => {
      reconnectAttempts++;
      store.dispatch(websocketActions.startConnecting());
    }, delay);
  };

  return (next) => (action) => {
    if (websocketActions.startConnecting.match(action)) {
      if (socket !== null) {
        return;
      }

      const currentUser = getCurrentCellStore();
      if (!currentUser?.student_id) {
        console.log('WebSocket connection failed: student_id not found.');
        return;
      }

      // Get JWT token from Cognito for secure authentication
      Auth.currentSession()
        .then((session) => {
          const token = session.getAccessToken().getJwtToken();
          
          // Pass JWT token and learnerId for secure authentication
          const learnerId = currentUser.student_id!; // Already validated above
          const wsUrl = `${process.env.REACT_APP_WEBSOCKET_URL}?token=${encodeURIComponent(token)}&learnerId=${encodeURIComponent(learnerId)}`;
          socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('WebSocket connection established.');
        reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        clearReconnectTimer(); // Clear any pending reconnect timer
        isManualDisconnect = false; // Reset manual disconnect flag
        store.dispatch(websocketActions.connectionEstablished());
      };

      socket.onmessage = ({ data }) => {
        try {
          const message = JSON.parse(data);
          if (message.notification && message.notification.type === 'BADGE_UNLOCKED') {
            console.log('Badge unlocked message received:', message);
            
            // Get cell settings to determine if we should dispatch
            const cell = getCurrentCellStore();
            const badgeId = message.notification?.metadata?.badge_id;
            const badgeCategory = message.notification?.metadata?.category;
            
            // Determine if badge belongs to training or phishing using constants
            const isTrainingBadge = BADGE_FILTER_CONFIGS.TRAINING_CATEGORIES.includes(badgeCategory) || 
                                   (badgeCategory === 'streak_and_special' && BADGE_FILTER_CONFIGS.TRAINING_STREAK_BADGES.includes(badgeId));
            const isPhishingBadge = BADGE_FILTER_CONFIGS.PHISHING_CATEGORIES.includes(badgeCategory) || 
                                   (badgeCategory === 'streak_and_special' && BADGE_FILTER_CONFIGS.PHISHING_STREAK_BADGES.includes(badgeId));
            
            // Only dispatch if badge type matches cell settings
            let shouldDispatch = false;
            
            if (cell?.show_training_data && cell?.show_phishing_data) {
              // Both enabled - show all badges
              shouldDispatch = true;
            } else if (cell?.show_training_data && !cell?.show_phishing_data) {
              // Only training - show training badges only
              shouldDispatch = isTrainingBadge;
            } else if (cell?.show_phishing_data && !cell?.show_training_data) {
              // Only phishing - show phishing badges only
              shouldDispatch = isPhishingBadge;
            } else {
              // Both disabled - show all badges (default behavior)
              shouldDispatch = true;
            }
            
            if (shouldDispatch) {
              console.log('Dispatching badge events for badge:', badgeId, 'category:', badgeCategory);
              (store.dispatch as any)(getBadgeAchievementsEvents({}));
              (store.dispatch as any)(getBadgesEvents({}));
            } else {
              console.log('Badge unlocked but filtered out based on cell settings:', badgeId, 'category:', badgeCategory);
            }
          } else { // Keep old logic for backward compatibility
            console.log('Unknown message type:', message);
          }
        } catch (error) {
          console.error('Failed to parse websocket message:', error);
        }
      };

      socket.onerror = (event) => {
        console.error('WebSocket error:', event);
        store.dispatch(websocketActions.error(event));
      };

      socket.onclose = (event) => {
        console.log('WebSocket connection closed.', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        store.dispatch(websocketActions.disconnected());
        socket = null;
        
        // Handle 429 Too Many Connections - wait longer before reconnecting
        if (event.code === 429 || event.reason?.includes('429') || event.reason?.includes('Too many connections')) {
          console.log('WebSocket 429 error: Too many connections. Waiting before reconnect...');
          // Wait 5 seconds before attempting reconnect to allow stale connections to be cleaned up
          reconnectInterval = setTimeout(() => {
            reconnectAttempts = 0; // Reset attempts for fresh start
            store.dispatch(websocketActions.startConnecting());
          }, 5000);
          return;
        }
        
        // Only attempt reconnection if it wasn't a manual disconnect
        if (!isManualDisconnect) {
          attemptReconnect();
        }
      };
        })
        .catch((error) => {
          console.error('Failed to get authentication token for WebSocket:', error);
          store.dispatch(websocketActions.error(error));
        });
    }

    if (websocketActions.stopConnecting.match(action)) {
      isManualDisconnect = true; // Mark as manual disconnect to prevent reconnection
      clearReconnectTimer(); // Clear any pending reconnection attempts
      reconnectAttempts = 0; // Reset reconnect attempts
      
      if (socket) {
        socket.close();
      }
    }

    // Handle logout - disconnect WebSocket when user logs out
    if (logoutEvents.pending.match(action) || logoutEvents.fulfilled.match(action)) {
      console.log('User logging out, disconnecting WebSocket');
      isManualDisconnect = true; // Mark as manual disconnect to prevent reconnection
      clearReconnectTimer(); // Clear any pending reconnection attempts
      reconnectAttempts = 0; // Reset reconnect attempts
      
      if (socket) {
        socket.close();
      }
    }

    // Handle workspace change - disconnect and reconnect with new workspace
    if (websocketActions.reconnectForWorkspaceChange.match(action)) {
      console.log('Workspace changed, reconnecting WebSocket with new workspace');
      
      // Close existing connection
      if (socket) {
        isManualDisconnect = true; // Temporarily mark as manual to prevent auto-reconnect
        socket.close();
        socket = null;
      }
      
      // Clear any pending reconnection attempts
      clearReconnectTimer();
      reconnectAttempts = 0;
      
      // Reset manual disconnect flag and start new connection
      isManualDisconnect = false;
      
      // Dispatch disconnected state
      store.dispatch(websocketActions.disconnected());
      
      // Start new connection with updated workspace (student_id)
      // Small delay to ensure clean disconnect
      setTimeout(() => {
        store.dispatch(websocketActions.startConnecting());
      }, 100);
    }

    next(action);
  };
};

export default websocketMiddleware;
